// light-cart-drawer.js — standalone cart drawer with optional Checkout+ (Redo) upsell
// Reads config from window.TL_REDO_PRODUCT (set by sections/light-cart-drawer.liquid).
// Communicates with bundle-builder.js via custom events:
//   listens for 'tl:cart:open'  — opens drawer and re-renders
//   listens for 'tl:toast'      — shows a toast notification (detail.html)
(function () {

  /* ---- Helpers ---- */
  function mcFmt(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
  }

  /* ---- Config ---- */
  const REDO_PRODUCT_HANDLE = window.TL_REDO_PRODUCT || 'free-returns-coverage-3';

  const REDO_TIERS = [
    { max:  5000, price: 218 },
    { max:  7500, price: 318 },
    { max: 10000, price: 333 },
    { max: 12500, price: 348 },
    { max: 15000, price: 373 },
    { max: Infinity, price: 398 },
  ];

  /* ---- State ---- */
  let _redoVariants    = null;
  let _currentCartTotal = 0;

  /* ---- Redo helpers ---- */
  function redoPriceForCart(cartCents) {
    for (const t of REDO_TIERS) {
      if (cartCents < t.max) return t.price;
    }
    return 398;
  }

  async function getRedoVariants() {
    if (_redoVariants) return _redoVariants;
    try {
      const p = await fetch('/products/' + REDO_PRODUCT_HANDLE + '.js').then(r => r.json());
      _redoVariants = p.variants.sort((a, b) => a.price - b.price);
    } catch (_) { _redoVariants = []; }
    return _redoVariants;
  }

  async function redoEnable() {
    const cart = await fetch('/cart.js').then(r => r.json());
    const regularTotal = cart.items
      .filter(i => i.sku !== 'x-redo')
      .reduce((s, i) => s + i.line_price, 0);
    const cartCents = regularTotal || _currentCartTotal;
    const targetPrice = redoPriceForCart(cartCents);
    const variants   = await getRedoVariants();
    const variant    = variants.find(v => v.price === targetPrice) || variants[variants.length - 1];
    const existing   = cart.items.find(i => i.sku === 'x-redo');
    if (existing && existing.variant_id !== variant.id) {
      await fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: existing.key, quantity: 0 }) });
      await fetch('/cart/add.js',    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: variant.id, quantity: 1 }) });
    } else if (!existing) {
      await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: variant.id, quantity: 1 }) });
    }
    await fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes: { redo_opted_in_from_cart: 'true' } }) });
  }

  async function redoDisable() {
    const cart = await fetch('/cart.js').then(r => r.json());
    const item = cart.items.find(i => i.sku === 'x-redo');
    if (item) {
      await fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: item.key, quantity: 0 }) });
    }
    await fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes: { redo_opted_in_from_cart: 'false' } }) });
  }

  /* ---- Badge ---- */
  function updateBadge(count) {
    const el = document.getElementById('tl-cart-count');
    if (!el) return;
    el.textContent = count;
    el.hidden = count === 0;
  }

  /* ---- Item rendering ---- */
  function mcGroupItems(items) {
    const bundles = new Map();
    const singles = [];
    items.forEach(item => {
      const bid = item.properties && item.properties._curated_bundle_identifier;
      if (bid) {
        if (!bundles.has(bid)) bundles.set(bid, []);
        bundles.get(bid).push(item);
      } else {
        singles.push(item);
      }
    });
    return { bundles, singles };
  }

  function mcImgUrl(src) {
    if (!src) return null;
    return src.replace(/\.(jpg|jpeg|png|gif|webp)(\?|$)/i, '_96x120_crop_center.$1$2');
  }

  function mcPlaceholderImg() {
    return `<div class="mc-item__img mc-item__img--ph"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#bbb" stroke-width="1.5" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg></div>`;
  }

  const BUNDLE_ICON_URL = 'https://cdn.shopify.com/s/files/1/0078/6825/2273/files/8__boxer_briefs_icon_88dad81c-490e-41f2-8e53-374487ca658d.svg?v=1762269465';

  function mcBundleRow(groupItems, bundleId) {
    const first         = groupItems[0];
    const title         = (first.properties && first.properties._title) || first.product_title;
    const originalTotal  = groupItems.reduce((s, i) => s + i.original_line_price, 0);
    const discountedTotal = groupItems.reduce((s, i) => s + i.line_price, 0);
    const hasDiscount    = originalTotal > discountedTotal;

    const pairsHtml = groupItems.map(item => {
      const fabric   = item.product_title.replace(/boxer\s*brief/i, '').replace(/trunk/i, '').trim();
      const varParts = (item.variant_title || '').split(' / ');
      const color    = varParts[0] || '';
      const size     = (item.properties && item.properties._bundle_size_scheme) || varParts[1] || '';
      return `<div class="mc-item__pair"><span class="mc-item__qty">${item.quantity || 1}x</span> ${fabric} · ${color} · ${size}</div>`;
    }).join('');

    const priceHtml = hasDiscount
      ? `<span class="mc-item__original">${mcFmt(originalTotal)}</span><span class="mc-item__price mc-item__price--disc">${mcFmt(discountedTotal)}</span>`
      : `<span class="mc-item__price">${mcFmt(discountedTotal)}</span>`;

    const keys = JSON.stringify(groupItems.map(i => i.key));
    return `
      <div class="mc-item" data-bid="${bundleId}">
        <img class="mc-item__img mc-item__img--icon" src="${BUNDLE_ICON_URL}" alt="Bundle" loading="lazy">
        <div class="mc-item__info">
          <div class="mc-item__head">
            <div class="mc-item__title">${title}</div>
            <button class="mc-item__remove" data-keys='${keys}'>✕</button>
          </div>
          <div class="mc-item__prices">${priceHtml}</div>
          <div class="mc-item__pairs">${pairsHtml}</div>
        </div>
      </div>`;
  }

  function mcSingleRow(item) {
    const imgSrc = item.featured_image?.url ? mcImgUrl(item.featured_image.url) : null;
    const imgHtml = imgSrc
      ? `<img class="mc-item__img" src="${imgSrc}" alt="${item.product_title}" loading="lazy">`
      : mcPlaceholderImg();
    return `
      <div class="mc-item">
        ${imgHtml}
        <div class="mc-item__info">
          <div class="mc-item__head">
            <div class="mc-item__title">${item.product_title}</div>
            <button class="mc-item__remove" data-keys='${JSON.stringify([item.key])}'>✕</button>
          </div>
          <div class="mc-item__prices"><span class="mc-item__price">${mcFmt(item.final_line_price)}</span></div>
          ${item.variant_title ? `<div class="mc-item__size">${item.variant_title}</div>` : ''}
        </div>
      </div>`;
  }

  /* ---- Render cart ---- */
  async function mcRender() {
    const body       = document.getElementById('tl-mc-body');
    const foot       = document.getElementById('tl-mc-foot');
    const subtotalEl = document.getElementById('tl-mc-subtotal');
    if (!body) return;

    body.innerHTML = '<div class="mc__loading">Loading…</div>';

    let cart;
    try {
      cart = await fetch('/cart.js').then(r => r.json());
    } catch (_) {
      body.innerHTML = '<div class="mc__empty">Couldn\'t load cart. Please try again.</div>';
      if (foot) foot.setAttribute('hidden', '');
      return;
    }

    const redoItem     = cart.items.find(i => i.sku === 'x-redo');
    const regularItems = cart.items.filter(i => i.sku !== 'x-redo');
    const redoPriceEl  = document.getElementById('tl-mc-redo-price');

    updateBadge(regularItems.reduce((s, i) => s + i.quantity, 0));

    if (regularItems.length === 0) {
      body.innerHTML = `<div class="mc__empty">Your cart is empty.<span class="mc__empty-cta" id="tl-mc-build">Build a bundle</span></div>`;
      if (foot) foot.setAttribute('hidden', '');
      document.getElementById('tl-mc-build')?.addEventListener('click', () => {
        closeMiniCart();
        const el = document.getElementById('tl-bundle');
        if (el) window.scrollTo({ top: el.offsetTop - 60, behavior: 'smooth' });
      });
      return;
    }

    const { bundles, singles } = mcGroupItems(regularItems);
    let html = '';
    bundles.forEach((items, id) => { html += mcBundleRow(items, id); });
    singles.forEach(item => { html += mcSingleRow(item); });
    body.innerHTML = html;

    let displaySubtotal = 0;
    bundles.forEach(items => { displaySubtotal += items.reduce((s, i) => s + i.line_price, 0); });
    singles.forEach(item  => { displaySubtotal += item.final_line_price; });

    _currentCartTotal = displaySubtotal;
    if (redoPriceEl)  redoPriceEl.textContent = mcFmt(redoItem ? redoItem.price : redoPriceForCart(displaySubtotal));
    if (subtotalEl)   subtotalEl.textContent   = mcFmt(displaySubtotal);

    const noteEl = document.getElementById('tl-mc-note');
    if (noteEl) {
      noteEl.textContent = displaySubtotal >= 12500 ? 'Enter your new customer code at checkout to get 20% off' : '';
    }
    if (foot) foot.removeAttribute('hidden');

    body.querySelectorAll('.mc-item__remove').forEach(btn => {
      btn.addEventListener('click', async () => {
        const keys = JSON.parse(btn.dataset.keys);
        btn.disabled = true;
        btn.textContent = '…';
        try {
          for (const key of keys) {
            await fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: key, quantity: 0 }) });
          }
        } catch (_) {}
        mcRender();
      });
    });
  }

  /* ---- Open / close ---- */
  function gorgiasEl() { return document.querySelector('#gorgias-web-messenger-container'); }

  function lockScroll() {
    if (window.disableBodyScroll) window.disableBodyScroll();
    else document.body.style.overflow = 'hidden';
  }

  function unlockScroll() {
    if (window.enableBodyScroll) window.enableBodyScroll();
    else document.body.style.overflow = '';
  }

  function openMiniCart() {
    const g = gorgiasEl();
    if (g) g.style.zIndex = '-1';
    document.getElementById('tl-mc')?.classList.add('mc--open');
    document.getElementById('tl-mc-overlay')?.classList.add('mc--open');
    lockScroll();
    mcRender();
  }

  function closeMiniCart() {
    const g = gorgiasEl();
    if (g) g.style.zIndex = '';
    document.getElementById('tl-mc')?.classList.remove('mc--open');
    document.getElementById('tl-mc-overlay')?.classList.remove('mc--open');
    unlockScroll();
  }

  function openRedoModal() {
    const m = document.getElementById('tl-redo-modal');
    if (m) m.hidden = false;
  }

  function closeRedoModal() {
    const m = document.getElementById('tl-redo-modal');
    if (m) m.hidden = true;
  }

  /* ---- Toast ---- */
  let _toastTimer;
  function showToast(html) {
    const el = document.getElementById('tl-toast');
    if (!el) return;
    el.innerHTML = html;
    el.hidden = false;
    clearTimeout(_toastTimer);
    _toastTimer = setTimeout(() => { el.hidden = true; }, 2600);
  }

  /* ---- Init ---- */
  function init() {
    document.getElementById('tl-cart-btn')?.addEventListener('click', openMiniCart);
    document.getElementById('tl-mc-close')?.addEventListener('click', closeMiniCart);
    document.getElementById('tl-mc-overlay')?.addEventListener('click', closeMiniCart);
    document.getElementById('tl-mc-redo-info')?.addEventListener('click', openRedoModal);
    document.getElementById('tl-redo-modal-close')?.addEventListener('click', closeRedoModal);
    document.getElementById('tl-redo-modal-bd')?.addEventListener('click', closeRedoModal);
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') { closeRedoModal(); closeMiniCart(); }
    });

    document.getElementById('tl-mc-checkout-plus')?.addEventListener('click', async e => {
      e.preventDefault();
      const btn = e.currentTarget;
      btn.textContent = '…';
      btn.style.pointerEvents = 'none';
      try { await redoEnable(); } catch (_) {}
      window.location.href = '/checkout';
    });

    document.getElementById('tl-mc-checkout-skip')?.addEventListener('click', async e => {
      e.preventDefault();
      const link = e.currentTarget;
      link.textContent = '…';
      link.style.pointerEvents = 'none';
      try { await redoDisable(); } catch (_) {}
      window.location.href = '/checkout';
    });

    window.addEventListener('pageshow', function (e) {
      if (!e.persisted) return;
      const btn  = document.getElementById('tl-mc-checkout-plus');
      const skip = document.getElementById('tl-mc-checkout-skip');
      if (btn)  { btn.textContent  = 'CHECKOUT+'; btn.style.pointerEvents = ''; }
      if (skip) { skip.textContent = 'Checkout without free returns & package protection'; skip.style.pointerEvents = ''; }
    });

    // Seed badge on load
    fetch('/cart.js').then(r => r.json()).then(cart => {
      updateBadge(cart.items.filter(i => i.sku !== 'x-redo').reduce((s, i) => s + i.quantity, 0));
    }).catch(() => {});

    // Cross-component events from bundle-builder.js
    window.addEventListener('tl:cart:open', openMiniCart);
    window.addEventListener('tl:toast',     e => showToast(e.detail?.html || ''));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
